import React, { ReactPortal, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface WindowComponentProps {
    children: React.ReactNode;
    usePortal: boolean;
    onClosePortal: () => void;
}

const WindowComponent: React.FC<WindowComponentProps> = ({ children, usePortal, onClosePortal }) => {
    const [container, setContainer] = useState<HTMLDivElement>(() => document.createElement('div'));
    const [externalWindow, setExternalWindow] = useState<Window | null>(null);
    const [portal, setPortal] = useState<ReactPortal | null>(null);

    const copyStyles = (sourceDoc: Document, targetDoc: Document) => {
        Array.from(sourceDoc.styleSheets).forEach((styleSheet) => {
            if (styleSheet.cssRules) {
                const newStyleEl = sourceDoc.createElement('style');

                Array.from(styleSheet.cssRules).forEach((cssRule) => {
                    newStyleEl.appendChild(sourceDoc.createTextNode(cssRule.cssText));
                });

                targetDoc.head.appendChild(newStyleEl);
            } else if (styleSheet.href) {
                const newLinkEl = sourceDoc.createElement('link');

                newLinkEl.rel = 'stylesheet';
                newLinkEl.href = styleSheet.href;
                targetDoc.head.appendChild(newLinkEl);
            }
        });
    };

    useEffect(() => {
        setPortal(createPortal(children, container));

        return () => {
            setPortal(null);
        };
    }, [children, container]);

    useEffect(() => {
        if (usePortal) {
            const _container = document.createElement('div');
            setContainer(_container);

            // Open a new browser window and store a reference to it
            const newWindow = window.open('', '', 'width=1600,height=800,left=200,top=200');
            copyStyles(document, newWindow?.document);

            // Append the container <div> to the body of the new window
            newWindow?.document.body.appendChild(_container);
            newWindow.addEventListener('beforeunload', () => {
                onClosePortal();
            });
            setExternalWindow(newWindow);

            return () => {
                newWindow?.close();
            };
        } else if (!usePortal && externalWindow) {
            externalWindow.close();
        }
    }, [usePortal]);

    if (usePortal) {
        return portal;
    } else {
        return <>{children}</>;
    }
};

export default WindowComponent;
